import { useState, useEffect, useCallback } from 'react';
import { getWeatherStations, type Sensor } from '../services/sensors';
import { useWebSocket } from './useWebSocket';
import { useSession } from '../context/SessionContext';
import { DEMO_WEATHER_STATIONS, type WeatherStation } from '../data/demo';

/**
 * Adapta la proyección `Sensor` (backend) a la forma `WeatherStation` que ya
 * consume WeatherPage.tsx — así el componente no necesita saber si viene de
 * datos reales o demo. Campos ausentes en `metrics` quedan `undefined`
 * (nunca 0): ver el comentario en `WeatherStation.current` en
 * apps/web/src/data/demo/index.ts.
 */
function toWeatherStation(s: Sensor): WeatherStation {
  const m = (s.lastReading?.metrics ?? {}) as Record<string, number | undefined>;
  return {
    id: s.id,
    imei: s.imei,
    name: s.name,
    lat: s.lat,
    lng: s.lng,
    // El componente solo maneja good/warn/alert (no 'offline').
    status: s.status === 'offline' ? 'alert' : s.status,
    current: {
      temperature: m.temperature,
      humidity: m.humidity,
      windSpeed: m.windSpeed,
      windDirLabel: '—', // wind_direction excluido — ver WEATHER_METRIC_KEY_MAP en sensor-view.ts
      radiation: m.radiation,
      rain: m.rain,
      pressure: m.pressure,
      dewPoint: undefined, // sin equivalente en la tabla hex del decoder Xiaoma
      uvIndex: m.uvIndex,
    },
    forecast: [], // sin fuente predictiva real — WeatherPage.tsx no debe usar esto para estaciones reales
  };
}

export function useWeatherStations() {
  const [stations, setStations] = useState<WeatherStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [error, setError] = useState(false);
  const { ready: sessionReady, isDemoTenant } = useSession();

  // OJO: el push de telemetría en vivo manda las claves crudas del decoder
  // (`wind_velocity`, `solar_radiation`...), no las claves de UI
  // (`windSpeed`, `radiation`...) — mismo gap ya conocido del lado Soil.
  // Este merge no traduce esas claves a propósito (arreglarlo es cambio
  // aparte, cross-cutting); las tarjetas de clima se actualizan en el
  // próximo refetch completo, no en vivo.
  const handleWebSocketMessage = useCallback((type: string, data: any) => {
    if (type === 'telemetry') {
      setStations((prev) =>
        prev.map((s) =>
          s.imei === data.imei
            ? { ...s, current: { ...s.current, ...data.reading?.metrics } }
            : s
        )
      );
    }
  }, []);

  const { subscribe } = useWebSocket(handleWebSocketMessage);

  // Mismo criterio que useSensors.ts (ver el comentario largo ahí): solo el
  // tenant Demo ve DEMO_WEATHER_STATIONS. Acá importa más que en suelo — el
  // seed de Demo (seed-demo-telemetry.ts) genera frames Yantai de suelo, con
  // profundidad, así que Demo NO tiene estaciones de clima reales en la base
  // y su página de clima depende de verdad de este fallback.
  const loadStations = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const data = await getWeatherStations();
      const usable = data.filter(s => s.lat != null && s.lng != null && s.lastReading);
      if (usable.length > 0) {
        setStations(usable.map(toWeatherStation));
        setIsDemo(false);
        subscribe(usable.map(s => s.imei));
      } else if (isDemoTenant) {
        setStations(DEMO_WEATHER_STATIONS);
        setIsDemo(true);
      } else {
        setStations([]);
        setIsDemo(false);
      }
    } catch {
      setStations([]);
      setIsDemo(false);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isDemoTenant, subscribe]);

  useEffect(() => {
    if (!sessionReady) return;
    loadStations();
  }, [sessionReady, loadStations]);

  return { stations, loading: loading || !sessionReady, isDemo, error, refresh: loadStations };
}
